import {
	Button,
	Card,
	CardContent,
	CardHeader,
	Collapse,
	Icon,
	IconButton,
	makeStyles,
	Typography
} from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { ExpandMoreOutlined } from '@material-ui/icons'
import clsx from 'clsx'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { filterAddOnsByTimeBlock, EMOJIS, BOARDING, DAYCARE, COMPATIBILITY_SESSION } from '../../utils'
import ReportDialog from '../Dialogs/Visit/ReportDialog'
import AddOnsTable from '../Tables/AddOnsTable'
import MedicationsTable from '../Tables/MedicationsTable'
import * as Constants from '../../utils/constants'
import useSelectedFacility from '../../hooks/useSelectedFacility'

const useStyles = makeStyles((theme) => ({
	card: {
		backgroundColor: '#F9F9F9',
		boxShadow: 'none'
	},
	header: {
		padding: '13px 24px',
		'& .MuiCardHeader-action': {
			margin: 0
		},
		'& .MuiCardHeader-title': {
			textTransform: 'none'
		}
	},
	expand: {
		transform: 'rotate(0deg)',
		marginLeft: 'auto',
		transition: theme.transitions.create('transform', {
			duration: theme.transitions.duration.shortest
		}),
		padding: 0
	},
	expandOpen: {
		transform: 'rotate(180deg)'
	},
	sectionsWrapper: {
		display: 'flex',
		justifyContent: 'space-between',
		flexWrap: 'wrap',
		maxWidth: '68%'
	},
	reportLink: {
		//display: "flex",
		color: '#6B4AD6',
		fontSize: '16px',
		justifyContent: 'flex-start',
		fontWeight: '600',
		'& i': {
			fontSize: '16px',
			marginRight: '16px',
			color: '#6B4AD6',
			lineHeight: '20px'
		},
		'&:hover': {
			textDecoration: 'none'
		},
		width: '100%',
		height: '100%',
		paddingLeft: '20px'
	},
	reports: {
		textTransform: 'capitalize',
		display: 'flex',
		fontSize: '16px',
		fontWeight: '600',
		'& i': {
			padding: '20px',
			fontSize: '16px',
			lineHeight: '20px'
		},
		'&:hover': {
			textDecoration: 'none'
		}
	},
	noReportsWrapper: {
		backgroundColor: '#F3F3F3',
		borderRadius: '6px',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		padding: '1.75rem'
	},
	reportItemWrapper: {
		border: '1px solid #E5E5E5',
		borderRadius: '6px',
		marginBottom: '10px'
	},
	reportContainer: {
		display: 'flex',
		flexDirection: 'column',
		padding: '8px',
		paddingLeft: '0px'
	},
	reportTitle: {
		textTransform: 'uppercase',
		fontWeight: '800',
		fontSize: '12px'
	},
	reportSubtext: {
		fontWeight: '600',
		fontSize: '14px'
	},
	cardContent: { display: 'flex', gap: '24px', padding: 0 },
	reportsColumn: { width: '29%', paddingLeft: '24px', minWidth: '200px' },
	tablesColumn: { flex: 1, marginRight: '12px' },
	tableHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
		marginBottom: '14px'
		// backgroundColor: 'white',
		// padding: '0 5px'
	}
}))

const VisitPetDetailDayDropdown = (props) => {
	const theme = useTheme()
	const classes = useStyles(theme)
	const { date, pet, visit, setIsAddAddonShown } = props
	const [isExpanded, setIsExpanded] = useState(false)
	const [isShown, setIsShown] = useState(false)
	const [selectedReport, selectReport] = useState(null)
	const [dailyAddOns, setDailyAddOns] = useState([])
	const [medications, setDailyMedications] = useState([])

	const allowedApptTypeWithMeds = [BOARDING, COMPATIBILITY_SESSION, DAYCARE]
	const isMedAllowed = allowedApptTypeWithMeds.includes(visit?.type)

	const isBoarding = visit?.type === BOARDING

	const isFirstDayOfVisit =
		visit?.type === DAYCARE || (isBoarding && visit?.dates?.[0] === moment(date).format('YYYY-MM-DD'))
	const isLastDayOfVisit =
		visit?.type === DAYCARE ||
		(isBoarding && visit?.dates?.[visit.dates.length - 1] === moment(date).format('YYYY-MM-DD'))

	const { allStaff } = useSelectedFacility()

	useEffect(() => {
		getAddOns()
	}, [pet?.add_on_services, pet?.food_schedule, visit?.medications, pet?.medication])

	const dropoffTask = visit?.related_tasks?.find((task) => task.type === 'dropoff')
	const pickupTask = visit?.related_tasks?.find((task) => task.type === 'pickup')

	const getAddOns = () => {
		let addOns = []
		const meds = []
		let date = moment(props.date).format('YYYY-MM-DD')
		const sortOrder = {
			morning: 0,
			midday: 1,
			evening: 2,
			night: 3
		}
		if (pet?.add_on_services) {
			addOns = pet.add_on_services.filter(
				(add_on_service) =>
					add_on_service?.date &&
					moment(add_on_service.date).isSame(moment(date)) &&
					add_on_service.visit_id === visit._id
			)
		}

		if (visit?.medications) {
			visit.medications.forEach((med) => {
				if (med.date === date && med.pet_id === pet._id) {
					const medDetails = pet?.medication?.find((m) => m._id === med.med_id)
					meds.push({
						...medDetails,
						...med,
						name: med.name || medDetails?.name,
						dosage: med.dosage || medDetails?.dosage,
						frequency: med.frequency || medDetails?.frequency
					})
				}
			})
		}

		// get meds and meals for a boarding visit
		if (isMedAllowed) {
			//get meds
			if (pet.medication)
				pet.medication
					.filter((med) => med.active !== false)
					.forEach((med, i) => {
					if (med.times_of_day && !visit.not_present_meds.includes(med._id)) {
						med.times_of_day.forEach((ti, i) => {
							let alreadyInArr = meds.find(
								(m) =>
									m.med_id === med._id &&
									m.pet_id === pet._id &&
									m.date === date &&
									m.time === ti
							)
							if (!alreadyInArr) {
								meds.push({
									...med,
									date,
									time: ti,
									pet_id: pet._id,
									med_id: med._id,
									visit_id: visit._id
								})
							}
						})
					}
				})
		}

		if (isBoarding) {
			//get meals
			// @TODO. This may be adding meals in that the client didn't select
			// if the appointment was created via the wizard.
			// Also, it may add in meals that are invalid, such as a morning meal on
			// the first day when the dog doesn't have daycare and early dropoff, etc.
			if (pet.food_schedule) {
				pet.food_schedule.forEach((t, i) => {
					let find = addOns.find((a) => {
						return a.category === 'meal' && a.pet_id === pet._id && a.date === date && a.time === t
					})
					if (!find) {
						const newAddOn = {
							pet_id: pet._id,
							category: 'meal',
							name: 'Meal',
							date: date,
							time: t,
							visit_id: visit._id
						}
						addOns.push(newAddOn)
					}
				})
			}
		}

		const filteredAddOns = addOns
			.filter((a) => filterAddOnsByTimeBlock(a, visit))
			.filter((a) => {
				let status = visit.pet_status.find((s) => s.pet_id === pet._id)
				let isCheckedOut = status && status.checked_out
				let isCompleted = a.completed || (a.status && a.status === 'completed')

				return isCheckedOut ? !a.skipped && isCompleted : !a.skipped
			})
			.sort((a, b) => {
				return sortOrder[a.time] - sortOrder[b.time]
			})

		const filteredMeds = meds
			.filter((a) => filterAddOnsByTimeBlock(a, visit))
			.filter((a) => {
				let status = visit.pet_status.find((s) => s.pet_id === pet._id)
				let isCheckedOut = status && status.checked_out
				let isCompleted = a.completed || (a.status && a.status === 'completed')

				return isCheckedOut ? !a.skipped && isCompleted : !a.skipped
			})
			.sort((a, b) => {
				return sortOrder[a.time] - sortOrder[b.time]
			})

		setDailyAddOns(filteredAddOns)
		setDailyMedications(filteredMeds)
	}

	const renderReports = () => {
		let ourItems = []
		//build array of our items to display

		if (dropoffTask && isFirstDayOfVisit) {
			const queueItem = dropoffTask.queue?.find((qI) => qI.pet_id === pet._id)
			if (!queueItem) {
				return null
			}
			const { staff_id = '', claimed_at } = queueItem
			const staffMember = allStaff?.find((staff) => staff._id === staff_id) || {
				first_name: 'ERROR',
				last_name: ''
			}
			ourItems.push({
				title: 'Dropoff Claimed',
				time: `${moment(claimed_at).format('MMM D h:mma')}`,
				type: 'dropoff',
				claimed_by: `${staffMember.first_name} ${staffMember.last_name?.charAt(0)}`
			})
		}

		if (visit) {
			if (visit.checkin_time) {
				if (moment(visit.checkin_time).isSame(moment(date), 'date'))
					ourItems.push({
						title: 'Checked In',
						time: moment(visit.checkin_time).format('MMM DD h:mma')
					})
			}

			if (visit.body_check_entry) {
				let bodyCheck = visit.body_check_entry[pet._id]
				//this only adds the body check if it has a timeSubmitted, so old body checks will not display
				if (
					bodyCheck &&
					bodyCheck.timeSubmitted &&
					moment(bodyCheck.timeSubmitted).isSame(moment(date), 'date')
				) {
					ourItems.push({
						title: 'Entry Body Check',
						time: moment(bodyCheck.timeSubmitted).format('h:mma')
					})
				}
			}

			if (visit.type === 'grooming') {
				if (visit.grooming_report_sent) {
					ourItems.push({
						title: 'Client Report',
						time: moment(visit.grooming_report_sent_time).format('h:mma'),
						report: { ...visit, report_type: 'client' }
					})
				}

				if (visit.grooming_team_report_notes || visit.behavior_notes) {
					ourItems.push({
						title: 'Internal Report',
						report: { ...visit, report_type: 'internal' }
					})
				}
			}

			if (visit.reports) {
				let { reports } = visit

				let ourReports = reports.filter((r) => {
					if (moment(date).isSame(moment(r.date)) && r.pet_id === pet._id) return true
					return false
				})

				ourReports.forEach((report) => {
					const { timeSent, report_category } = report
					let title = 'Client Report'
					if (report_category === Constants.FACILITY_VISIT_RELATED_TRAINING) {
						title = 'Play & Study Report'
					}
					if (timeSent) {
						ourItems.push({
							title,
							time: moment(report.timeSent).format('h:mma'),
							report: report
						})
					}
				})
			}

			if (pickupTask && isLastDayOfVisit) {
				const queueItem = pickupTask.queue?.find((qI) => qI.pet_id === pet._id)
				if (!queueItem) {
					return null
				}

				const { staff_id = '', claimed_at } = queueItem
				const staffMember = allStaff?.find((staff) => staff._id === staff_id) || {
					first_name: 'ERROR',
					last_name: ''
				}
				ourItems.push({
					title: 'Pickup Claimed',
					time: `${moment(claimed_at).format('MMM D h:mma')}`,
					type: 'pickup',
					claimed_by: `${staffMember.first_name} ${staffMember.last_name?.charAt(0)}`
				})
			}

			if (visit.body_check_exit) {
				let bodyCheck = visit.body_check_exit[pet._id]
				//this only adds the body check if it has a timeSubmitted, so old body checks w/o timeSubmitted will not display
				if (
					bodyCheck &&
					bodyCheck.timeSubmitted &&
					moment(bodyCheck.timeSubmitted).isSame(moment(date), 'date')
				) {
					ourItems.push({
						title: 'Exit Body Check',
						time: moment(bodyCheck.timeSubmitted).format('h:mma')
					})
				}
			}

			const petGuardian = visit && visit.pet_status && visit.pet_status.find((pg) => pg.pet_id === pet._id)
			if (petGuardian?.checkout_time && moment(petGuardian?.checkout_time).isSame(moment(date), 'date')) {
				ourItems.push({
					title: 'Checked Out',
					time: moment(petGuardian?.checkout_time).format('MMM D h:mma'),
					pet_guardian: petGuardian?.picked_up_by || '-'
				})
			} else {
				if (visit.checkout_time && moment(visit?.checkout_time).isSame(moment(date), 'date')) {
					ourItems.push({
						title: 'Checked Out',
						time: moment(visit?.checkout_time).format('MMM D h:mma'),
						pet_guardian: '-'
					})
				}
			}
		}

		const status = visit.pet_status.find((s) => s.pet_id === pet._id)
		const medicationSignature = status?.medication_signature_url

		if (medicationSignature && isLastDayOfVisit) {
			ourItems.push({
				type: 'medication_signature',
				title: 'Medication Signature',
				url: medicationSignature
			})
		}

		return ourItems.length > 0 ? (
			ourItems.map((item, i) => {
				return (
					<div className={classes.reportItemWrapper} key={`ouritem-${i}`}>
						{item.report ? (
							<Button
								variant="text"
								className={classes.reportLink}
								onClick={() => {
									selectReport(item.report)
									setIsShown(true)
								}}>
								<i className="fas fa-file" />
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										justifyContent: item.time ? 'flex-start' : 'center',
										padding: '8px',
										paddingLeft: '0px'
									}}>
									<Typography className={classes.reportTitle}>{item.title}</Typography>
									{item.time && (
										<Typography
											style={{
												textTransform: 'lowercase',
												textAlign: 'left',
												fontWeight: '600',
												fontSize: '14px'
											}}>
											{item.time}
										</Typography>
									)}
								</div>
							</Button>
						) : (
							<div className={classes.reports} key={`ouritem-${i}`}>
								{item.title.includes('Checked') || item.title.includes('Body Check') ? (
									<i
										className="fas fa-check-circle"
										style={{
											color: '#777679'
										}}
									/>
								) : ['dropoff', 'pickup'].includes(item.type) ? (
									<i style={{ fontSize: 18, lineHeight: '18px', paddingTop: '17px' }}>
										{item.type === 'dropoff' ? EMOJIS.dropoff : EMOJIS.pickup}
									</i>
								) : (
									<i
										className="fas fa-file"
										style={{
											color: '#6B4AD6'
										}}
									/>
								)}
								<div className={classes.reportContainer}>
									<Typography className={classes.reportTitle}>{item.title}</Typography>
									{item.type === 'medication_signature' ? (
										<img style={{ width: '70%' }} src={item.url} />
									) : null}
									<Typography className={classes.reportSubtext}>{item.time}</Typography>
									{item.title.includes('Checked Out') ? (
										<Typography className={classes.reportSubtext}>
											Picked up by {item.pet_guardian}
										</Typography>
									) : null}
									{item.claimed_by > '' ? (
										<Typography className={classes.reportSubtext}>
											Claimed by {item.claimed_by}
										</Typography>
									) : null}
								</div>
							</div>
						)}
					</div>
				)
			})
		) : (
			<div className={classes.noReportsWrapper}>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center'
					}}>
					<Icon className="fas fa-inbox" style={{ color: '#777679', overflow: 'visible' }} />
					<Typography style={{ color: '#777679', fontSize: '16px', fontWeight: '600' }}>
						Nothing to report!
					</Typography>
				</div>
			</div>
		)
	}

	return (
		<Card className={classes.card}>
			<CardHeader
				className={classes.header}
				title={date}
				onClick={() => setIsExpanded(!isExpanded)}
				action={
					<IconButton
						className={clsx(classes.expand, {
							[classes.expandOpen]: isExpanded
						})}
						onClick={() => setIsExpanded(!isExpanded)}
						aria-expanded={isExpanded}
						aria-label="show more">
						<ExpandMoreOutlined style={{ fill: '#777679' }} />
					</IconButton>
				}
			/>
			<Collapse in={isExpanded} timeout="auto" unmountOnExit>
				<CardContent className={classes.cardContent}>
					<div className={classes.reportsColumn}>
						<Typography variant="overline">Reporting</Typography>
						<div style={{ marginTop: '24px' }}> {renderReports()} </div>
					</div>
					<div className={classes.tablesColumn}>
						<>
							<div className={classes.tableHeader}>
								<Typography variant="overline">Enrichment & Food Schedule</Typography>
								<Button onClick={() => setIsAddAddonShown(date)}>
									<Typography
										style={{
											color: '#6B4AD6',
											fontWeight: '600px',
											fontSize: '16px',
											textTransform: 'capitalize'
										}}>
										Add Activity
									</Typography>
								</Button>
							</div>
							<AddOnsTable
								pet={pet}
								addOns={dailyAddOns}
								forceOneAddonSelected={isBoarding}
								grooming={visit.type === 'grooming' ? true : false}
								shouldShowMainGroomingService={true}
								visit={visit}
							/>
						</>
						{medications.length > 0 ? (
							<div style={{ marginTop: '1rem' }}>
								<div className={classes.tableHeader}>
									<Typography variant="overline">Medication Schedule</Typography>
								</div>
								<MedicationsTable pet={pet} medications={medications} visit={visit} />
							</div>
						) : null}
					</div>
				</CardContent>
			</Collapse>
			<ReportDialog
				isShown={isShown}
				setIsShown={setIsShown}
				pet={pet}
				visit={visit}
				report={selectedReport}
				date={date}
			/>
		</Card>
	)
}

export default VisitPetDetailDayDropdown
